import Phaser from 'phaser';
import { DESIGN, COLORS, SIM } from '@/config';
import type { BattleScene } from '@/scenes/BattleScene';
import type { TargetMode } from '@/data/schema/common';
import { displayLevel } from '@/battle/Upgrades';
import { sfx } from '@/audio/Sfx';
import { progress } from '@/game/Progress';

const TARGET_CYCLE: TargetMode[] = ['first', 'last', 'strongest', 'nearest'];
const TARGET_LABEL: Record<TargetMode, string> = {
  first: 'First',
  last: 'Last',
  strongest: 'Strong',
  nearest: 'Near',
  flyer: 'Flyer',
};

interface PanelButton {
  cost?: number; // for affordability restyle each frame
  disabled?: boolean; // hard-disabled (e.g. hero already deployed) — skip restyle
  setAffordable: (ok: boolean) => void; // dim when unaffordable
}

interface RowButton {
  label: string;
  cost?: number;
  disabled?: boolean;
  /** Optional medallion texture key shown above the label (deploy/branch). */
  icon?: string;
  onClick: () => void;
}

/**
 * HUD overlay — screen-space UI run in parallel with BattleScene. Reads the
 * battle's World each frame and drives the player actions. Rebuilds the slot
 * panel only when `battle.uiVersion` changes; restyles affordability per frame.
 */
export class HUDScene extends Phaser.Scene {
  private battle!: BattleScene;
  private statsText!: Phaser.GameObjects.Text;
  private pauseLabel!: Phaser.GameObjects.Text;
  private speedButtons: { value: number; bg: Phaser.GameObjects.Graphics; x: number; y: number; w: number; h: number }[] = [];

  private waveButton!: Phaser.GameObjects.Text;
  private panel!: Phaser.GameObjects.Container;
  private panelButtons: PanelButton[] = [];
  private lastUiVersion = -1;

  private banner!: Phaser.GameObjects.Container;
  private bannerText!: Phaser.GameObjects.Text;
  private starText!: Phaser.GameObjects.Text;
  private summaryText!: Phaser.GameObjects.Text;
  private lastOutcome = 'playing';

  private wavePreview!: Phaser.GameObjects.Container;
  private wavePreviewText!: Phaser.GameObjects.Text;
  private wavePreviewChips!: Phaser.GameObjects.Container;
  private lastPreviewKey = '';

  private hints!: Phaser.GameObjects.Container;
  private hintText!: Phaser.GameObjects.Text;
  private hintProgress!: Phaser.GameObjects.Text;
  private hintIndex = 0;

  constructor() {
    super('hud');
  }

  create(): void {
    this.battle = this.scene.get('battle') as BattleScene;
    this.lastUiVersion = -1;
    this.lastOutcome = 'playing';

    this.add.rectangle(0, 0, DESIGN.width, 56, 0x10131f, 0.7).setOrigin(0, 0);
    this.statsText = this.add.text(16, 14, '', {
      fontFamily: 'system-ui, sans-serif',
      fontSize: '24px',
      color: COLORS.text,
    });

    // Tempo controls (top-right).
    let x = DESIGN.width - 16;
    this.pauseLabel = this.makeTextButton(x - 52, 28, 'Pause', () => this.battle.togglePause());
    x -= 120;
    for (let i = SIM.speeds.length - 1; i >= 0; i--) {
      this.makeSpeedButton(x - 26, 28, SIM.speeds[i]);
      x -= 60;
    }

    this.makeTextButton(46, 84, '‹ Map', () => {
      this.scene.stop('battle');
      this.scene.stop('hud');
      this.scene.start('worldmap');
    });
    this.makeMuteButton(160, 84);

    // Primary wave button (bottom-centre).
    this.waveButton = this.add
      .text(DESIGN.width / 2, DESIGN.height - 60, '', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '28px',
        fontStyle: 'bold',
        color: '#10131f',
        backgroundColor: '#e0b04a',
        padding: { x: 22, y: 12 },
        align: 'center',
      })
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });
    this.waveButton.on('pointerup', () => this.battle.actCallWave());

    this.panel = this.add.container(0, 0).setDepth(50);

    this.buildWavePreview();
    this.buildBanner();
    this.buildHints();
  }

  // ---- wave preview (plan ahead — the core TD decision) --------------------

  private static readonly PREVIEW_W = 684;
  private static readonly PREVIEW_H = 72;

  private buildWavePreview(): void {
    const W = HUDScene.PREVIEW_W;
    const H = HUDScene.PREVIEW_H;
    const bg = this.add.graphics();
    bg.fillStyle(0x10131f, 0.8).fillRoundedRect(-W / 2, -H / 2, W, H, 12);
    bg.lineStyle(1.5, 0x7a5a2e, 0.9).strokeRoundedRect(-W / 2, -H / 2, W, H, 12);
    this.wavePreviewText = this.add
      .text(0, -H / 2 + 14, '', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '15px',
        fontStyle: 'bold',
        color: COLORS.accent,
        align: 'center',
      })
      .setOrigin(0.5);
    this.wavePreviewChips = this.add.container(0, 12);
    this.wavePreview = this.add
      .container(DESIGN.width / 2, 100, [bg, this.wavePreviewText, this.wavePreviewChips])
      .setDepth(40)
      .setVisible(false);
  }

  /** A threat-tag icon key for an enemy so players know what counter to bring. */
  private enemyTagIcon(enemyId: string): string | null {
    const def = this.battle.world.data.enemies.get(enemyId);
    if (!def) return null;
    if (def.flags.flying) return 'ui_tag_flying';
    if ((def.stats.armor ?? 0) >= 0.3) return 'ui_tag_armored';
    if ((def.stats.hp ?? 0) <= 50) return 'ui_tag_swarm';
    return null;
  }

  /** Rebuild the chip row: one medallion + ×count (+ threat icon) per group. */
  private rebuildPreviewChips(groups: { enemyId: string; count: number }[]): void {
    this.wavePreviewChips.removeAll(true);
    const w = this.battle.world;

    // Pass 1: build each chip's objects and measure its width.
    const chips: { objs: Phaser.GameObjects.GameObject[]; width: number }[] = [];
    for (const g of groups) {
      const def = w.data.enemies.get(g.enemyId);
      const tokenKey = `enemy_${g.enemyId}`;
      const objs: Phaser.GameObjects.GameObject[] = [];
      let x = 0;
      if (this.textures.exists(tokenKey)) {
        objs.push(this.add.image(0, 0, tokenKey).setDisplaySize(26, 26).setData('ox', x + 13));
        x += 30;
      }
      const label = this.add
        .text(x, 0, `${g.count}× ${def?.name ?? g.enemyId}`, {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '15px',
          color: COLORS.text,
        })
        .setOrigin(0, 0.5);
      objs.push(label);
      x += label.width + 4;
      const tag = this.enemyTagIcon(g.enemyId);
      if (tag && this.textures.exists(tag)) {
        objs.push(this.add.image(0, 0, tag).setDisplaySize(18, 18).setData('ox', x + 9));
        x += 20;
      }
      chips.push({ objs, width: x });
    }

    // Pass 2: lay the chips out centred, with gaps.
    const gap = 26;
    const total = chips.reduce((s, c) => s + c.width, 0) + gap * (chips.length - 1);
    let cursor = -total / 2;
    for (const c of chips) {
      for (const o of c.objs) {
        const obj = o as Phaser.GameObjects.Image & Phaser.GameObjects.Text;
        // Images carry their centre offset in data; text was positioned at its left.
        const ox = obj.getData?.('ox');
        obj.x = cursor + (typeof ox === 'number' ? ox : obj.x);
        this.wavePreviewChips.add(obj);
      }
      cursor += c.width + gap;
    }
  }

  private updateWavePreview(): void {
    const w = this.battle.world;
    const showPrep = w.phase === 'prep';
    const showInter = w.phase === 'intermission';
    if (!showPrep && !showInter) {
      this.wavePreview.setVisible(false);
      this.lastPreviewKey = '';
      return;
    }
    const next = showPrep ? w.data.level.waves[0] : w.nextWave();
    if (!next) {
      this.wavePreview.setVisible(false);
      return;
    }
    const num = showPrep ? 1 : w.waveIndex + 2;
    const key = `${w.phase}:${num}`;
    if (key !== this.lastPreviewKey) {
      this.lastPreviewKey = key;
      const lead = showPrep ? 'First wave' : `Next · wave ${num}/${w.totalWaves()}`;
      this.wavePreviewText.setText(lead);
      this.rebuildPreviewChips(next.groups);
    }
    this.wavePreview.setVisible(true);
  }

  // ---- first-run hints (lightweight onboarding) ----------------------------

  private static readonly HINTS = [
    'Tap a glowing + slot to deploy. Slots are universal — any unit fits anywhere, so you decide what goes where, and how many.',
    'Upgrading a unit makes it SPECIALIZE: Footmen → Shieldwall (tough wall) or Reaver (sunders armor + Exposes held foes). Archers → Marksman (heavy armor-piercing) or Skirmisher (two archers — melts swarms & flyers).',
    'Counters matter: armored Brutes & bosses shrug off light shots — crack them with Mages, Marksman, or Reaver sunder. Storm Harpies fly over blockers — any Archer, Mage or Frost hits air. Hold tanks at a chokepoint, then focus fire.',
    'Deploy your Pilgrim-Warden (♛), your champion, to anchor a chokepoint — then press ▶ Start Battle. Watch the wave preview to plan ahead.',
  ];

  private buildHints(): void {
    const W = 600;
    const H = 250;
    const bg = this.add.graphics();
    bg.fillStyle(0x10131f, 0.96).fillRoundedRect(-W / 2, -H / 2, W, H, 16);
    bg.lineStyle(2, 0xe0b04a, 1).strokeRoundedRect(-W / 2, -H / 2, W, H, 16);
    const title = this.add
      .text(0, -H / 2 + 30, 'How to hold the Steps', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '24px',
        fontStyle: 'bold',
        color: COLORS.accent,
      })
      .setOrigin(0.5);
    this.hintText = this.add
      .text(0, -8, '', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '19px',
        color: COLORS.text,
        align: 'center',
        wordWrap: { width: W - 64 },
        lineSpacing: 4,
      })
      .setOrigin(0.5);
    const next = this.add
      .text(0, H / 2 - 34, '', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '20px',
        fontStyle: 'bold',
        color: '#10131f',
        backgroundColor: '#e0b04a',
        padding: { x: 18, y: 8 },
      })
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });
    this.hintProgress = next;
    next.on('pointerup', () => {
      sfx.play('click');
      this.advanceHint();
    });
    this.hints = this.add
      .container(DESIGN.width / 2, DESIGN.height / 2, [bg, title, this.hintText, next])
      .setDepth(120)
      .setVisible(false);

    let seen = false;
    try {
      seen = globalThis.localStorage?.getItem('dc_hints_seen') === '1';
    } catch {
      seen = false;
    }
    if (!seen) {
      this.hintIndex = 0;
      this.showHint();
    }
  }

  private showHint(): void {
    const last = this.hintIndex >= HUDScene.HINTS.length - 1;
    this.hintText.setText(HUDScene.HINTS[this.hintIndex]);
    this.hintProgress.setText(last ? "Let's go ▶" : 'Next ›');
    this.hints.setVisible(true);
  }

  private advanceHint(): void {
    if (this.hintIndex >= HUDScene.HINTS.length - 1) {
      this.hints.setVisible(false);
      try {
        globalThis.localStorage?.setItem('dc_hints_seen', '1');
      } catch {
        /* ignore */
      }
      return;
    }
    this.hintIndex++;
    this.showHint();
  }

  // ---- the slot panel (picker / actions) -----------------------------------

  private rebuildPanel(): void {
    this.panel.removeAll(true);
    this.panelButtons = [];

    const slotId = this.battle.selectedSlotId;
    if (!slotId) return;
    const world = this.battle.world;
    const slot = world.slot(slotId);
    if (!slot) return;

    const cy = DESIGN.height - 150;
    const title = (s: string): void => {
      const t = this.add
        .text(DESIGN.width / 2, cy - 36, s, {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '18px',
          color: COLORS.text,
        })
        .setOrigin(0.5);
      this.panel.add(t);
    };

    const info = (s: string): void => {
      const t = this.add
        .text(DESIGN.width / 2, cy - 62, s, {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '15px',
          color: '#aab0d8',
        })
        .setOrigin(0.5);
      this.panel.add(t);
    };

    const unit = world.units.get(slotId);
    if (unit) {
      // --- actions on a deployed unit ---
      const def = world.data.units.get(unit.defId);
      const branchName = unit.branchId
        ? def?.branches.find((b) => b.id === unit.branchId)?.name
        : undefined;
      const name = branchName ?? def?.name ?? 'Unit';
      const level = def ? displayLevel(def, unit.tier, unit.branchId) : unit.tier + 1;
      title(`${def?.isHero ? '♛ ' : ''}${name}  ${'★'.repeat(level)}`);
      // Decompose what the player placed so the choice is legible.
      const s = unit.stats;
      if (unit.isMelee) {
        const squad = unit.soldiers.length;
        const cap = squad * unit.blockCount;
        const squadTag = squad > 1 ? ` · ×${squad} soldiers` : '';
        info(`HP ${Math.round(unit.maxHp)} · DMG ${Math.round(s.damage)} · holds ${cap}${squadTag}`);
      } else {
        const slows = (world.activeTierDef(slotId)?.onHitStatus ?? []).some((e) => e.type === 'slow');
        const extra = slows ? ' · slows' : s.armorPierce > 0 ? ` · pierce ${Math.round(s.armorPierce * 100)}%` : '';
        // Fire rate (attacks/sec) sits next to DMG — together they read as the unit's DPS.
        const rate = s.attackSpeed > 0 ? ` · ${s.attackSpeed.toFixed(1)}/s` : '';
        info(`DMG ${Math.round(s.damage)}${rate} · range ${Math.round(s.range)}${extra}`);
      }

      // Sell confirmation: the Sell tap swaps the action row for a deliberate
      // Keep / Sell choice so a fat-finger doesn't refund a key unit mid-wave.
      if (this.battle.pendingSell) {
        const refund = Math.floor(unit.goldSpent * (def?.sellRefund ?? 0.5));
        this.layoutRow(
          [
            { label: 'Keep ✗', onClick: () => this.battle.actCancelSell() },
            { label: `Sell ✓\n+${refund}g`, onClick: () => this.battle.actSell() },
          ],
          cy,
        );
        return;
      }

      const buttons: RowButton[] = [];
      const opt = world.upgradeOptions(slotId);
      if (opt?.kind === 'branch') {
        // One-time specialization choice: one button per path, previewing the
        // branch medallion the unit will become.
        for (const b of opt.options) {
          buttons.push({
            label: `→ ${b.name}\n${b.cost}g`,
            cost: b.cost,
            icon: `unit_${unit.defId}_${b.id}`,
            onClick: () => this.battle.actUpgrade(b.id),
          });
        }
      } else if (opt?.kind === 'upgrade') {
        buttons.push({ label: `Upgrade ▲\n${opt.cost}g`, cost: opt.cost, onClick: () => this.battle.actUpgrade() });
      } else {
        buttons.push({ label: 'Max ★', onClick: () => {} });
      }
      const refund = Math.floor(unit.goldSpent * (def?.sellRefund ?? 0.5));
      buttons.push({ label: `Sell\n+${refund}g`, onClick: () => this.battle.actBeginSell() });
      if (!unit.isMelee) {
        buttons.push({
          label: `Target\n${TARGET_LABEL[unit.targetMode]}`,
          onClick: () => {
            const i = TARGET_CYCLE.indexOf(unit.targetMode);
            this.battle.actSetTargeting(TARGET_CYCLE[(i + 1) % TARGET_CYCLE.length]);
          },
        });
      }
      this.layoutRow(buttons, cy);
    } else if (!world.isSlotUnlocked(slot)) {
      title(`Locked until wave ${slot.lockedUntilWave + 1}`);
    } else if (this.battle.draftUnitId) {
      // --- draft: a chosen unit awaiting confirmation (ghost shown on-board) ---
      const def = world.data.units.get(this.battle.draftUnitId);
      const t0 = def?.tiers[0];
      const st = t0?.stats;
      title(`${def?.isHero ? '♛ ' : ''}${def?.name ?? 'Unit'}`);
      if (def && st) {
        if (def.family === 'melee') {
          const squad0 = t0?.squadSize ?? def.squadSize;
          const cap0 = squad0 * (t0?.blockCount ?? def.blockCount);
          const squadTag = squad0 > 1 ? ` · ×${squad0} soldiers` : '';
          info(`HP ${Math.round((st.hp ?? 0) * squad0)} · DMG ${Math.round(st.damage ?? 0)} · holds ${cap0}${squadTag}`);
        } else {
          const rate = st.attackSpeed ? ` · ${st.attackSpeed.toFixed(1)}/s` : '';
          info(`DMG ${Math.round(st.damage ?? 0)}${rate} · range ${Math.round(st.range ?? 0)}`);
        }
      }
      const cost = t0?.goldCost ?? 0;
      this.layoutRow(
        [
          { label: 'Cancel ✗', onClick: () => this.battle.actCancelDeploy() },
          {
            label: `Build ✓\n${cost}g`,
            cost,
            icon: `unit_${this.battle.draftUnitId}`,
            onClick: () => this.battle.actConfirmDeploy(),
          },
        ],
        cy,
      );
    } else {
      // --- unit picker for an empty slot (universal: the whole roster) ---
      // Picking stages the unit as a draft (ghost + range) rather than building
      // it outright, so the player can confirm placement before spending gold.
      title('Deploy a unit');
      const opts: RowButton[] = world.deployableFor().map((d) => ({
        label: `${d.def.isHero ? '♛ ' : ''}${d.def.name}\n${d.atLimit ? '1/1 placed' : `${d.def.tiers[0].goldCost}g`}`,
        cost: d.atLimit ? undefined : d.def.tiers[0].goldCost,
        disabled: d.atLimit,
        icon: `unit_${d.def.id}`,
        onClick: d.atLimit ? () => {} : () => this.battle.actBeginDeploy(d.def.id),
      }));
      this.layoutRow(opts, cy);
    }
  }

  private layoutRow(buttons: RowButton[], cy: number): void {
    const n = buttons.length;
    if (n === 0) return;
    const gap = 10;
    // Shrink the buttons as needed so the whole row fits the portrait width.
    const w = Math.min(150, Math.floor((DESIGN.width - 24 - (n - 1) * gap) / n));
    const hasIcon = buttons.some((b) => b.icon != null && this.textures.exists(b.icon));
    const h = hasIcon ? 90 : 60;
    const total = n * w + (n - 1) * gap;
    // Anchor the row's bottom near cy so taller icon rows don't crowd the title.
    const y = cy + 8;
    let bx = DESIGN.width / 2 - total / 2 + w / 2;
    for (const b of buttons) {
      const { container, setAffordable } = this.makePanelButton(bx, y, w, h, b);
      this.panel.add(container);
      this.panelButtons.push({ cost: b.cost, disabled: b.disabled, setAffordable });
      bx += w + gap;
    }
  }

  /** A framed slot-panel button — marble fill, bronze top accent, optional
   *  medallion above the label. Returns an affordability dimmer. */
  private makePanelButton(
    x: number,
    y: number,
    w: number,
    h: number,
    b: RowButton,
  ): { container: Phaser.GameObjects.Container; setAffordable: (ok: boolean) => void } {
    const g = this.add.graphics();
    g.fillStyle(b.disabled ? 0x23263c : 0x2b3052, 1).fillRoundedRect(-w / 2, -h / 2, w, h, 9);
    g.lineStyle(2, b.disabled ? 0x3a3f5e : 0x565d8c, 1).strokeRoundedRect(-w / 2, -h / 2, w, h, 9);
    g.fillStyle(0x7a5a2e, b.disabled ? 0.35 : 0.85).fillRoundedRect(-w / 2 + 6, -h / 2 + 4, w - 12, 3, 2);

    const hasIcon = b.icon != null && this.textures.exists(b.icon);
    const children: Phaser.GameObjects.GameObject[] = [g];
    if (hasIcon) {
      // Buildings are tall, not square — fit within a box preserving the aspect
      // so the watchtower/barracks art isn't squashed into a circle's footprint.
      const src = this.textures.get(b.icon!).getSourceImage() as { width: number; height: number };
      const sc = Math.min(42 / src.width, 48 / src.height);
      children.push(
        this.add.image(0, -h / 2 + 30, b.icon!).setDisplaySize(src.width * sc, src.height * sc),
      );
    }
    children.push(
      this.add
        .text(0, hasIcon ? h / 2 - 22 : 0, b.label, {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '15px',
          fontStyle: 'bold',
          color: COLORS.text,
          align: 'center',
          wordWrap: { width: w - 10 },
        })
        .setOrigin(0.5),
    );

    const container = this.add.container(x, y, children);
    if (b.disabled) {
      container.setAlpha(0.5);
    } else {
      const zone = this.add.zone(0, 0, w, h).setInteractive({ useHandCursor: true });
      zone.on('pointerup', b.onClick);
      container.add(zone);
    }
    return {
      container,
      setAffordable: (ok: boolean) => {
        if (!b.disabled) container.setAlpha(ok ? 1 : 0.45);
      },
    };
  }

  // ---- banner --------------------------------------------------------------

  private buildBanner(): void {
    const bg = this.add.graphics();
    bg.fillStyle(0x10131f, 0.94).fillRoundedRect(-270, -135, 540, 270, 16);
    bg.lineStyle(2, 0x565d8c, 1).strokeRoundedRect(-270, -135, 540, 270, 16);
    this.bannerText = this.add
      .text(0, -88, '', { fontFamily: 'system-ui, sans-serif', fontSize: '40px', fontStyle: 'bold', color: COLORS.accent, align: 'center' })
      .setOrigin(0.5);
    this.starText = this.add
      .text(0, -30, '', { fontFamily: 'system-ui, sans-serif', fontSize: '44px', color: COLORS.accent })
      .setOrigin(0.5);
    this.summaryText = this.add
      .text(0, 36, '', { fontFamily: 'system-ui, sans-serif', fontSize: '18px', color: '#aab0d8', align: 'center', lineSpacing: 4 })
      .setOrigin(0.5);
    const replay = this.add
      .text(-72, 96, 'Replay ↻', { fontFamily: 'system-ui, sans-serif', fontSize: '24px', color: COLORS.text, backgroundColor: '#3b4066', padding: { x: 14, y: 6 } })
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });
    replay.on('pointerup', () => {
      sfx.play('click');
      this.banner.setVisible(false);
      this.lastOutcome = 'playing';
      this.lastUiVersion = -1;
      this.battle.restartBattle();
    });
    const toMap = this.add
      .text(96, 96, 'World Map ▶', { fontFamily: 'system-ui, sans-serif', fontSize: '24px', fontStyle: 'bold', color: '#10131f', backgroundColor: '#e0b04a', padding: { x: 14, y: 6 } })
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });
    toMap.on('pointerup', () => {
      sfx.play('click');
      this.scene.stop('battle');
      this.scene.stop('hud');
      this.scene.start('worldmap');
    });
    this.banner = this.add
      .container(DESIGN.width / 2, DESIGN.height / 2, [bg, this.bannerText, this.starText, this.summaryText, replay, toMap])
      .setVisible(false)
      .setDepth(100);
  }

  // ---- shared button helpers ----------------------------------------------

  private makeTextButton(x: number, y: number, label: string, onClick: () => void): Phaser.GameObjects.Text {
    const t = this.add
      .text(x, y, label, {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '22px',
        color: COLORS.text,
        backgroundColor: '#3b4066',
        padding: { x: 12, y: 6 },
      })
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });
    t.on('pointerup', () => {
      sfx.play('click');
      onClick();
    });
    return t;
  }

  /** Sound on/off toggle; reflects the persisted mute state from `sfx`. */
  private makeMuteButton(x: number, y: number): void {
    const label = (): string => (sfx.isMuted() ? '🔇 Off' : '🔊 On');
    const t = this.add
      .text(x, y, label(), {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '22px',
        color: COLORS.text,
        backgroundColor: '#3b4066',
        padding: { x: 12, y: 6 },
      })
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });
    t.on('pointerup', () => {
      const nowMuted = sfx.toggleMuted();
      t.setText(label());
      if (!nowMuted) sfx.play('click'); // audible confirmation when turning sound on
    });
  }

  private makeSpeedButton(x: number, y: number, value: number): void {
    const w = 50;
    const h = 36;
    const bg = this.add.graphics();
    this.add
      .text(x, y, `${value}×`, { fontFamily: 'system-ui, sans-serif', fontSize: '20px', fontStyle: 'bold', color: COLORS.text })
      .setOrigin(0.5)
      .setDepth(1);
    const zone = this.add.zone(x, y, w, h).setInteractive({ useHandCursor: true });
    zone.on('pointerup', () => {
      sfx.play('click');
      this.battle.setSpeed(value);
    });
    this.speedButtons.push({ value, bg, x, y, w, h });
  }

  // ---- per-frame update ----------------------------------------------------

  update(): void {
    const w = this.battle.world;

    this.statsText.setText(`❤ ${w.lives}    ◇ ${w.gold}g    Wave ${Math.max(0, w.waveIndex + (w.phase === 'prep' ? 0 : 1))}/${w.totalWaves()}    T${w.tier} ${w.difficultyName}`);
    this.pauseLabel.setText(this.battle.paused ? 'Resume' : 'Pause');

    for (const b of this.speedButtons) {
      const active = b.value === this.battle.speed && !this.battle.paused;
      b.bg.clear();
      b.bg.fillStyle(active ? 0x5ac0a0 : 0x3b4066, 1);
      b.bg.lineStyle(2, 0x565d8c, 1);
      b.bg.fillRoundedRect(b.x - b.w / 2, b.y - b.h / 2, b.w, b.h, 8);
      b.bg.strokeRoundedRect(b.x - b.w / 2, b.y - b.h / 2, b.w, b.h, 8);
    }

    // Primary wave button.
    if (w.phase === 'prep') {
      this.waveButton.setVisible(true).setText('▶  Start Battle').setBackgroundColor('#e0b04a');
    } else if (w.phase === 'intermission') {
      this.waveButton
        .setVisible(true)
        .setText(`Call Next Wave ▶   ⏱ ${Math.ceil(w.intermissionRemaining)}s`)
        .setBackgroundColor('#5ac0a0');
    } else if (w.phase === 'spawning') {
      this.waveButton.setVisible(true).setText(`⚔  Wave ${w.waveIndex + 1} of ${w.totalWaves()}`).setBackgroundColor('#3b4066');
    } else {
      this.waveButton.setVisible(false);
    }

    // Rebuild the slot panel only when something structural changed.
    if (this.battle.uiVersion !== this.lastUiVersion) {
      this.lastUiVersion = this.battle.uiVersion;
      this.rebuildPanel();
    }
    // Restyle affordability each frame (gold changes as enemies die).
    for (const b of this.panelButtons) {
      if (b.disabled || b.cost == null) continue;
      b.setAffordable(w.gold >= b.cost);
    }

    this.updateWavePreview();

    // Outcome banner.
    if (w.phase !== this.lastOutcome && (w.phase === 'won' || w.phase === 'lost')) {
      this.lastOutcome = w.phase;
      const wavesCleared = w.phase === 'won' ? w.totalWaves() : Math.max(0, w.waveIndex);
      if (w.phase === 'won') {
        // Persist the clear: best tier + stars, and unlock the next stop (which
        // flags it for the World Map's reveal animation).
        progress.recordClear(this.battle.level.id, w.tier, w.stars);
        this.bannerText.setText('Victory!').setColor(COLORS.accent);
        this.starText.setText('★'.repeat(w.stars) + '☆'.repeat(3 - w.stars));
        const lost = w.startingLives - w.lives;
        const flawless = lost === 0 ? '  ·  flawless!' : '';
        this.summaryText.setText(
          `All ${w.totalWaves()} waves held${flawless}\n` +
            `Lives ${w.lives}/${w.startingLives}  ·  Gold ${w.gold}`,
        );
      } else if (w.bossBreached) {
        this.bannerText.setText('Boss Broke Through!').setColor('#e06c6c');
        this.starText.setText('');
        this.summaryText.setText(
          `The boss reached the gate — the line has fallen.\n` +
            `Held ${wavesCleared}/${w.totalWaves()} waves  ·  Counter it before it breaks free.`,
        );
      } else {
        this.bannerText.setText('Gate Overrun!').setColor('#e06c6c');
        this.starText.setText('');
        this.summaryText.setText(
          `Held ${wavesCleared}/${w.totalWaves()} waves  ·  ${w.leaked} leaked\nRegroup and try a different mix.`,
        );
      }
      this.banner.setVisible(true);
    }
  }
}
